// WHAT IS THIS FILE? — the FE "Performance Table" reader. Bytes in, records + provenance out.
//
// The shape (TRAP 8):
//   line 1   "Performance Table"                                  <- title
//   line 2   "Pricing Spread: Bid-Bid,Price Range: ...,Currency: Pounds Sterling"
//            ⚠️ ONE quoted CSV field containing commas. A naive text.split(",") shreds it into
//            three fields and loses the association between key and value. It must go through a
//            real RFC-4180 parser and then be re-split on its OWN commas.
//   line 3   "Name","Custom Period Performance"                   <- header
//   line 4+  "<series name>",<value>                              <- data
//
// This parser does NOT classify and does NOT validate. It hands back records with their physical
// line numbers so that every downstream finding can point at a line a human can go and look at.

import type { FeProvenance } from "./types";

export interface CsvRecord {
  fields: string[];
  /** 1-based physical line on which this record STARTS. Correct across quoted newlines. */
  line: number;
}

/**
 * RFC-4180: double quotes, "" escapes, CRLF or LF, quoted fields may contain commas and newlines.
 * Wholly blank lines are dropped (FE pads the tail of some exports).
 *
 * Mirrors `parseCsv` in supabase/functions/reporting-golden-ingest/index.ts:257, with the addition
 * of line tracking.
 */
export function parseCsvRecords(text: string): CsvRecord[] {
  const out: CsvRecord[] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;
  let line = 1;
  let recordStartLine = 1;
  let sawAnyChar = false;

  const endField = () => {
    row.push(field);
    field = "";
  };

  const endRecord = () => {
    endField();
    const blank = row.length === 1 && row[0].trim() === "";
    if (!blank) out.push({ fields: row, line: recordStartLine });
    row = [];
    sawAnyChar = false;
  };

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (!sawAnyChar && c !== "\n" && c !== "\r") {
      recordStartLine = line;
      sawAnyChar = true;
    }
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        if (c === "\n") line++;
        field += c;
      }
      continue;
    }
    if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      endField();
    } else if (c === "\n") {
      endRecord();
      line++;
    } else if (c === "\r") {
      // swallow; the \n that follows ends the record
    } else {
      field += c;
    }
  }
  // Trailing record with no final newline.
  if (field !== "" || row.length > 0) endRecord();
  return out;
}

/** e.g. "30 Jun 2026" -> "2026-06-30". Returns null on anything it does not fully understand. */
const MONTHS: Record<string, string> = {
  jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06",
  jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12",
};

export function parseFeDate(s: string): string | null {
  const m = /^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/.exec(s.trim());
  if (!m) return null;
  const mm = MONTHS[m[2].slice(0, 3).toLowerCase()];
  if (!mm) return null;
  return `${m[3]}-${mm}-${m[1].padStart(2, "0")}`;
}

const CURRENCY_CODES: Record<string, string> = {
  "pounds sterling": "GBP",
  "pound sterling": "GBP",
  sterling: "GBP",
  "gb pound": "GBP",
  "us dollar": "USD",
  "us dollars": "USD",
  euro: "EUR",
  euros: "EUR",
};

/**
 * Parse line 2 into the provenance statement.
 *
 * `basisRaw` is the re-joined line, kept verbatim. Even when every parsed field below comes back
 * null, that string is the thing that makes the numbers attributable, so it is never discarded.
 */
export function parseProvenanceLine(fields: string[]): Omit<FeProvenance, "title" | "metricLabel"> {
  // If FE quoted the whole line we get one field; if it did not, we get several. Re-join either
  // way, then split on the line's own commas.
  const basisRaw = fields.join(",").trim();
  const parts = basisRaw
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  const extras: Record<string, string> = {};
  let pricingSpread: string | null = null;
  let priceRangeRaw: string | null = null;
  let currency: string | null = null;

  for (const part of parts) {
    const idx = part.indexOf(":");
    if (idx < 0) {
      extras[part] = "";
      continue;
    }
    const key = part.slice(0, idx).trim();
    const value = part.slice(idx + 1).trim();
    const k = key.toLowerCase();
    if (k === "pricing spread") pricingSpread = value;
    else if (k === "price range") priceRangeRaw = value;
    else if (k === "currency") currency = value;
    else extras[key] = value;
  }

  let windowStart: string | null = null;
  let windowEnd: string | null = null;
  if (priceRangeRaw) {
    const m = /from\s+(.+?)\s+to\s+(.+)$/i.exec(priceRangeRaw);
    if (m) {
      windowStart = parseFeDate(m[1]);
      windowEnd = parseFeDate(m[2]);
    }
  }

  const currencyCode = currency ? (CURRENCY_CODES[currency.trim().toLowerCase()] ?? null) : null;

  return {
    basisRaw,
    pricingSpread,
    priceRangeRaw,
    windowStart,
    windowEnd,
    currency,
    currencyCode,
    extras,
  };
}

export interface FeTable {
  /** True when lines 1-3 have the FE Performance Table shape. When false, nothing else is trusted. */
  shapeOk: boolean;
  /** Why the shape check failed, when it did. */
  shapeProblem: string | null;
  provenance: FeProvenance;
  /** Data records only — records[3..]. Preamble and header are excluded. */
  records: CsvRecord[];
  /** Every parsed record including preamble/header, for callers that want to see the raw grid. */
  allRecords: CsvRecord[];
}

/**
 * Read the whole table. Never throws: a file that is not an FE Performance Table comes back with
 * `shapeOk: false` and an empty `records`, and the caller turns that into a refusal.
 */
export function parseFePerformanceTable(csvText: string): FeTable {
  // Strip a leading UTF-8 BOM. Written as the \uFEFF ESCAPE, never as a literal BOM character:
  // a raw BOM in source is invisible, editors and formatters strip it silently, and the day one
  // does the regex quietly becomes /^/ — which matches everything and strips nothing, so every
  // BOM'd export would fail the line-3 header test and be refused as "not an FE Performance
  // Table". FE does emit BOM'd CSVs, so this line is load-bearing, not defensive decoration.
  const allRecords = parseCsvRecords(csvText.replace(/^\uFEFF/, ""));

  const empty: FeProvenance = {
    title: "",
    basisRaw: "",
    pricingSpread: null,
    priceRangeRaw: null,
    windowStart: null,
    windowEnd: null,
    currency: null,
    currencyCode: null,
    metricLabel: null,
    extras: {},
  };

  if (allRecords.length < 3) {
    return {
      shapeOk: false,
      shapeProblem: `expected at least 3 lines (title, basis, header) but found ${allRecords.length}`,
      provenance: empty,
      records: [],
      allRecords,
    };
  }

  const title = (allRecords[0].fields[0] ?? "").trim();
  const basis = parseProvenanceLine(allRecords[1].fields);
  const header = allRecords[2].fields.map((f) => f.trim());

  // The header test is the one that actually decides. FE always names column 1 "Name".
  const headerOk = (header[0] ?? "").toLowerCase() === "name" && header.length >= 2;

  const provenance: FeProvenance = {
    ...basis,
    title,
    metricLabel: headerOk ? (header[1] || null) : null,
  };

  if (!headerOk) {
    return {
      shapeOk: false,
      shapeProblem: `line 3 is not an FE header row — expected "Name","<metric>" but read ${JSON.stringify(
        allRecords[2].fields.slice(0, 4),
      )}`,
      provenance,
      records: [],
      allRecords,
    };
  }

  return {
    shapeOk: true,
    shapeProblem: null,
    provenance,
    records: allRecords.slice(3),
    allRecords,
  };
}
